// src/pages/PostsPage.tsx
// Page complète des posts avec commentaires, likes, et toutes les actions
// Optimisé avec React Query pour le caching et lazy loading des images
import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Button,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Divider,
    TextField,
    Collapse,
    Skeleton,
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SchoolIcon from '@mui/icons-material/School';
import PublicIcon from '@mui/icons-material/Public';
import ChurchIcon from '@mui/icons-material/Church';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ReportIcon from '@mui/icons-material/Report';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import CreatePostDialog from '../components/CreatePostDialog';
import { useInfinitePosts } from '../hooks/usePosts';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import LazyImage from '../components/common/LazyImage';
import ReactionPicker, { ReactionSummary, ReactionType } from '../components/common/ReactionPicker';
import { useToggleReaction } from '../hooks/useReactions';
import ReportDialog, { ReportReason } from '../components/common/ReportDialog';
import { useReport } from '../hooks/useReport';
import MentionInput, { MentionText } from '../components/common/MentionInput';

// Types
interface Media {
    id: number;
    file_url: string;
    thumbnail_url?: string;
    file_type: 'image' | 'video' | 'audio';
    media_type?: string;
    caption?: string;
}

interface PostUser {
    id: number;
    name: string;
    full_name?: string;
    profile_picture?: string;
}

interface Comment {
    id: number;
    content: string;
    created_at: string;
    user_id: number;
    user?: PostUser;
    author_name?: string;
    author_avatar_url?: string;
    reactions_count: number;
    has_liked?: boolean;
    parent_id?: number | null;
    replies_count?: number;
    replies?: Comment[];
}

interface ReactionsByType {
    like?: number;
    love?: number;
    pray?: number;
    amen?: number;
    sad?: number;
}

interface Post {
    id: number;
    user_id?: number;
    title?: string;
    content: string;
    post_type: string;
    visibility: string;
    ethiopian_month: number;
    ethiopian_day: number;
    ethiopian_year?: number;
    created_at: string;
    updated_at?: string;
    user?: PostUser;
    author_name?: string;
    author_id?: number;
    author_avatar_url?: string;
    media?: Media[];
    reactions_count?: number;
    reactions_by_type?: ReactionsByType;
    user_reaction?: ReactionType | null;
    likes_count?: number;
    comments_count?: number;
    shares_count?: number;
    is_featured?: boolean;
    has_liked?: boolean;
    moderation_status?: string;
}

const POST_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    reflection: { label: 'Réflexion', icon: <LightbulbOutlinedIcon fontSize="small" />, color: '#2196F3' },
    prayer: { label: 'Prière', icon: <AutoAwesomeIcon fontSize="small" />, color: '#9C27B0' },
    testimony: { label: 'Témoignage', icon: <FavoriteIcon fontSize="small" />, color: '#E91E63' },
    teaching: { label: 'Enseignement', icon: <SchoolIcon fontSize="small" />, color: '#FF9800' },
};

const VISIBILITY_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    public: { label: 'Public', icon: <PublicIcon fontSize="small" />, color: '#4caf50' },
    church: { label: 'Église', icon: <ChurchIcon fontSize="small" />, color: '#2196f3' },
    private: { label: 'Privé', icon: <LockIcon fontSize="small" />, color: '#9e9e9e' },
};

const ETHIOPIAN_MONTHS = [
    'Meskerem', 'Tikimt', 'Hidar', 'Tahsas', 'Tir', 'Yekatit',
    'Megabit', 'Miyazya', 'Ginbot', 'Sene', 'Hamle', 'Nehase', 'Pagume'
];

// Composant pour afficher la grille d'images (style Facebook)
const MediaGrid: React.FC<{ media: Media[] }> = ({ media }) => {
    const [fullscreenImage, setFullscreenImage] = React.useState<string | null>(null);
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

    const images = media.filter(m => (m.file_type === 'image' || m.media_type?.toLowerCase() === 'image'));
    const videos = media.filter(m => (m.file_type === 'video' || m.media_type?.toLowerCase() === 'video'));

    if (images.length === 0 && videos.length === 0) return null;

    const handleImageClick = (url: string, index: number) => {
        setFullscreenImage(url);
        setCurrentImageIndex(index);
    };

    const handleCloseFullscreen = () => {
        setFullscreenImage(null);
    };

    const handlePrevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
            setFullscreenImage(images[currentImageIndex - 1].file_url);
        }
    };

    const handleNextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentImageIndex < images.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
            setFullscreenImage(images[currentImageIndex + 1].file_url);
        }
    };

    const renderImages = () => {
        if (images.length === 1) {
            return (
                <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                    <LazyImage
                        src={images[0].file_url}
                        alt=""
                        thumbnail={images[0].thumbnail_url}
                        height={450}
                        objectFit="cover"
                        borderRadius={8}
                        onClick={() => handleImageClick(images[0].file_url, 0)}
                        rootMargin="300px"
                    />
                </Box>
            );
        }

        if (images.length === 2) {
            return (
                <Box sx={{ mt: 2, display: 'flex', gap: 0.5, borderRadius: 2, overflow: 'hidden' }}>
                    {images.map((img, idx) => (
                        <Box key={idx} sx={{ flex: 1, height: 280 }}>
                            <LazyImage
                                src={img.file_url}
                                alt=""
                                thumbnail={img.thumbnail_url}
                                height="100%"
                                objectFit="cover"
                                onClick={() => handleImageClick(img.file_url, idx)}
                                rootMargin="300px"
                            />
                        </Box>
                    ))}
                </Box>
            );
        }

        if (images.length === 3) {
            return (
                <Box sx={{ mt: 2, display: 'flex', gap: 0.5, borderRadius: 2, overflow: 'hidden', height: 320 }}>
                    <Box sx={{ flex: 1.2, height: '100%' }}>
                        <LazyImage
                            src={images[0].file_url}
                            alt=""
                            thumbnail={images[0].thumbnail_url}
                            height="100%"
                            objectFit="cover"
                            onClick={() => handleImageClick(images[0].file_url, 0)}
                            rootMargin="300px"
                        />
                    </Box>
                    <Box sx={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {images.slice(1).map((img, idx) => (
                            <Box key={idx} sx={{ flex: 1 }}>
                                <LazyImage
                                    src={img.file_url}
                                    alt=""
                                    thumbnail={img.thumbnail_url}
                                    height="100%"
                                    objectFit="cover"
                                    onClick={() => handleImageClick(img.file_url, idx + 1)}
                                    rootMargin="300px"
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            );
        }

        // 4+ images
        return (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, borderRadius: 2, overflow: 'hidden' }}>
                {images.slice(0, 4).map((img, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            width: 'calc(50% - 2px)',
                            height: 200,
                            position: 'relative',
                        }}
                    >
                        <LazyImage
                            src={img.file_url}
                            alt=""
                            thumbnail={img.thumbnail_url}
                            height="100%"
                            objectFit="cover"
                            onClick={() => handleImageClick(img.file_url, idx)}
                            rootMargin="300px"
                        />
                        {idx === 3 && images.length > 4 && (
                            <Box sx={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background-color 0.2s',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                                pointerEvents: 'none',
                            }}>
                                <Typography variant="h3" color="white" fontWeight={700}>
                                    +{images.length - 4}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        );
    };

    const renderVideos = () => {
        if (videos.length === 0) return null;

        return (
            <Box sx={{ mt: images.length > 0 ? 1 : 2 }}>
                {videos.map((video, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: '#000',
                            position: 'relative',
                            mb: idx < videos.length - 1 ? 1 : 0,
                        }}
                    >
                        <video
                            src={video.file_url}
                            poster={video.thumbnail_url}
                            controls
                            playsInline
                            style={{
                                width: '100%',
                                maxHeight: 450,
                                display: 'block',
                            }}
                        />
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <>
            {renderImages()}
            {renderVideos()}

            {/* Fullscreen Image Modal */}
            <Dialog
                open={!!fullscreenImage}
                onClose={handleCloseFullscreen}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        m: 0,
                    }
                }}
                sx={{
                    '& .MuiBackdrop-root': {
                        bgcolor: 'rgba(0,0,0,0.95)',
                    },
                }}
                onClick={handleCloseFullscreen}
            >
                <Box sx={{ position: 'relative' }}>
                    {/* Close button */}
                    <IconButton
                        onClick={handleCloseFullscreen}
                        sx={{
                            position: 'absolute',
                            top: -40,
                            right: 0,
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Main image */}
                    <img
                        src={fullscreenImage || ''}
                        alt=""
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            borderRadius: 8,
                        }}
                    />

                    {/* Navigation arrows */}
                    {images.length > 1 && (
                        <>
                            {currentImageIndex > 0 && (
                                <IconButton
                                    onClick={handlePrevImage}
                                    sx={{
                                        position: 'absolute',
                                        left: -50,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        width: 44,
                                        height: 44,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                    }}
                                >
                                    <ChevronLeftIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                            )}
                            {currentImageIndex < images.length - 1 && (
                                <IconButton
                                    onClick={handleNextImage}
                                    sx={{
                                        position: 'absolute',
                                        right: -50,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        width: 44,
                                        height: 44,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                    }}
                                >
                                    <ChevronRightIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                            )}
                        </>
                    )}

                    {/* Image counter */}
                    {images.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: -35,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.875rem',
                        }}>
                            {currentImageIndex + 1} / {images.length}
                        </Box>
                    )}
                </Box>
            </Dialog>
        </>
    );
};

// Composant Commentaire avec support des réponses imbriquées
const CommentItem: React.FC<{
    comment: Comment;
    currentUserId?: number;
    onLike: (commentId: number) => void;
    onDelete: (commentId: number) => void;
    onEdit: (comment: Comment) => void;
    onReply: (parentId: number, authorName: string) => void;
    isAdmin: boolean;
    depth?: number;
    maxDepth?: number;
}> = ({ comment, currentUserId, onLike, onDelete, onEdit, onReply, isAdmin, depth = 0, maxDepth = 3 }) => {
    const [showReplies, setShowReplies] = useState(depth < 1); // Auto-expand first level
    const isOwn = currentUserId === comment.user_id;
    const authorName = comment.author_name || comment.user?.name || comment.user?.full_name || 'Anonyme';
    const authorAvatar = comment.author_avatar_url || comment.user?.profile_picture;
    const hasReplies = comment.replies && comment.replies.length > 0;
    const repliesCount = comment.replies_count || comment.replies?.length || 0;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `${diffMins} min`;
        if (diffHours < 24) return `${diffHours}h`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1 }}>
            <Avatar
                src={authorAvatar}
                sx={{
                    width: depth > 0 ? 28 : 32,
                    height: depth > 0 ? 28 : 32,
                    fontSize: depth > 0 ? '0.75rem' : '0.875rem'
                }}
            >
                {authorName.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ bgcolor: '#f0f2f5', borderRadius: 2, px: 1.5, py: 1, display: 'inline-block', maxWidth: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: depth > 0 ? '0.8rem' : '0.875rem' }}>
                        {authorName}
                    </Typography>
                    <Box sx={{ fontSize: depth > 0 ? '0.85rem' : '0.875rem' }}>
                        <MentionText text={comment.content} />
                    </Box>
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 0.5, pl: 1 }} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                        {formatDate(comment.created_at)}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ cursor: 'pointer', fontWeight: comment.has_liked ? 600 : 400, color: comment.has_liked ? '#e91e63' : 'text.secondary', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => onLike(comment.id)}
                    >
                        J'aime {comment.reactions_count > 0 && `(${comment.reactions_count})`}
                    </Typography>
                    {depth < maxDepth && (
                        <Typography
                            variant="caption"
                            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => onReply(comment.id, authorName)}
                        >
                            Répondre
                        </Typography>
                    )}
                    {(isOwn || isAdmin) && (
                        <>
                            <Typography
                                variant="caption"
                                sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { textDecoration: 'underline' } }}
                                onClick={() => onEdit(comment)}
                            >
                                Modifier
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ cursor: 'pointer', color: 'error.main', '&:hover': { textDecoration: 'underline' } }}
                                onClick={() => onDelete(comment.id)}
                            >
                                Supprimer
                            </Typography>
                        </>
                    )}
                </Stack>

                {/* Toggle replies */}
                {hasReplies && (
                    <Typography
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                            pl: 1,
                            '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => setShowReplies(!showReplies)}
                    >
                        {showReplies ? '▼' : '▶'} {repliesCount} réponse{repliesCount > 1 ? 's' : ''}
                    </Typography>
                )}

                {/* Nested replies */}
                {hasReplies && showReplies && (
                    <Box sx={{ mt: 1, borderLeft: '2px solid', borderColor: 'divider', pl: 1.5 }}>
                        {comment.replies!.map((reply) => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                currentUserId={currentUserId}
                                onLike={onLike}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                onReply={onReply}
                                isAdmin={isAdmin}
                                depth={depth + 1}
                                maxDepth={maxDepth}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Composant PostCard complet
const PostCard: React.FC<{
    post: Post;
    currentUserId?: number;
    isAdmin: boolean;
    onRefresh: () => void;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
}> = ({ post, currentUserId, isAdmin, onRefresh, onSuccess, onError }) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState<Comment[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editCommentDialog, setEditCommentDialog] = useState<Comment | null>(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [replyToComment, setReplyToComment] = useState<number | null>(null);
    const [replyToAuthor, setReplyToAuthor] = useState<string | null>(null);
    const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([]);

    // Réactions
    const [currentReaction, setCurrentReaction] = useState<ReactionType | null>(
        post.user_reaction || (post.has_liked ? 'like' : null)
    );
    const [reactionsByType, setReactionsByType] = useState<ReactionsByType>(
        post.reactions_by_type || { like: post.reactions_count || post.likes_count || 0 }
    );
    const totalReactions = Object.values(reactionsByType).reduce((sum, count) => sum + (count || 0), 0);

    const toggleReaction = useToggleReaction();

    // Signalement
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const reportMutation = useReport();

    const handleReport = async (reason: ReportReason, details?: string) => {
        await reportMutation.mutateAsync({
            targetType: 'post',
            targetId: post.id,
            reason,
            details,
        });
        onSuccess('Post signalé avec succès');
    };

    const typeConfig = POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.reflection;
    const visConfig = VISIBILITY_CONFIG[post.visibility] || VISIBILITY_CONFIG.public;
    const authorName = post.author_name || post.user?.name || post.user?.full_name || 'Anonyme';
    const authorAvatar = post.author_avatar_url || post.user?.profile_picture;
    const isOwnPost = currentUserId === (post.author_id || post.user_id || post.user?.id);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `Il y a ${diffMins} min`;
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        if (diffDays < 7) return `Il y a ${diffDays}j`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    const getEthiopianDate = () => {
        const monthName = ETHIOPIAN_MONTHS[post.ethiopian_month - 1] || `Mois ${post.ethiopian_month}`;
        return `${post.ethiopian_day} ${monthName}`;
    };

    // Charger les commentaires
    const loadComments = async () => {
        setLoadingComments(true);
        try {
            const response = await api.get(`/post-comments/post/${post.id}`);
            setComments(response.data || []);
        } catch (err) {
            console.error('Error loading comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleToggleComments = () => {
        if (!showComments && comments.length === 0) {
            loadComments();
        }
        setShowComments(!showComments);
    };

    // Réaction au post
    const handleReact = (reactionType: ReactionType | null) => {
        const prevReaction = currentReaction;
        const prevReactionsByType = { ...reactionsByType };

        // Mise à jour optimiste
        setCurrentReaction(reactionType);
        setReactionsByType(prev => {
            const updated = { ...prev };
            // Décrémenter l'ancienne réaction
            if (prevReaction && updated[prevReaction]) {
                updated[prevReaction] = Math.max(0, (updated[prevReaction] || 0) - 1);
            }
            // Incrémenter la nouvelle réaction
            if (reactionType) {
                updated[reactionType] = (updated[reactionType] || 0) + 1;
            }
            return updated;
        });

        // Appel API
        toggleReaction.mutate(
            {
                targetId: post.id,
                targetType: 'post',
                reactionType,
                currentReaction: prevReaction,
            },
            {
                onError: () => {
                    // Rollback en cas d'erreur
                    setCurrentReaction(prevReaction);
                    setReactionsByType(prevReactionsByType);
                    onError('Erreur lors de la réaction');
                },
            }
        );
    };

    // Like comment
    const handleLikeComment = async (commentId: number) => {
        const comment = comments.find(c => c.id === commentId);
        if (!comment) return;

        setComments(prev => prev.map(c =>
            c.id === commentId
                ? { ...c, has_liked: !c.has_liked, reactions_count: c.has_liked ? c.reactions_count - 1 : c.reactions_count + 1 }
                : c
        ));

        try {
            await api.post(`/post-comments/${commentId}/reactions/like/toggle`);
        } catch (err) {
            setComments(prev => prev.map(c =>
                c.id === commentId ? { ...c, has_liked: comment.has_liked, reactions_count: comment.reactions_count } : c
            ));
        }
    };

    // Ajouter commentaire ou réponse
    const handleSubmitComment = async () => {
        if (!newComment.trim()) return;
        setSubmittingComment(true);
        try {
            const payload: { post_id: number; content: string; parent_id?: number; mentioned_user_ids?: number[] } = {
                post_id: post.id,
                content: newComment.trim(),
            };
            if (replyToComment) {
                payload.parent_id = replyToComment;
            }
            if (mentionedUserIds.length > 0) {
                payload.mentioned_user_ids = mentionedUserIds;
            }
            await api.post('/post-comments/', payload);
            setNewComment('');
            setReplyToComment(null);
            setReplyToAuthor(null);
            setMentionedUserIds([]);
            loadComments();
            onSuccess(replyToComment ? 'Réponse ajoutée' : 'Commentaire ajouté');
        } catch (err) {
            onError('Erreur lors de l\'ajout');
        } finally {
            setSubmittingComment(false);
        }
    };

    // Répondre à un commentaire
    const handleReply = (parentId: number, authorName: string) => {
        setReplyToComment(parentId);
        setReplyToAuthor(authorName);
        setNewComment(`@${authorName} `);
    };

    // Annuler la réponse
    const cancelReply = () => {
        setReplyToComment(null);
        setReplyToAuthor(null);
        setNewComment('');
    };

    // Supprimer commentaire
    const handleDeleteComment = async (commentId: number) => {
        try {
            await api.delete(`/post-comments/${commentId}`);
            setComments(prev => prev.filter(c => c.id !== commentId));
            onSuccess('Commentaire supprimé');
        } catch (err) {
            onError('Erreur lors de la suppression');
        }
    };

    // Modifier commentaire
    const handleEditComment = async () => {
        if (!editCommentDialog || !editCommentText.trim()) return;
        try {
            await api.put(`/post-comments/${editCommentDialog.id}`, { content: editCommentText.trim() });
            setComments(prev => prev.map(c =>
                c.id === editCommentDialog.id ? { ...c, content: editCommentText.trim() } : c
            ));
            setEditCommentDialog(null);
            onSuccess('Commentaire modifié');
        } catch (err) {
            onError('Erreur lors de la modification');
        }
    };

    // Copier texte
    const handleCopyText = () => {
        const text = post.title ? `${post.title}\n\n${post.content}` : post.content;
        navigator.clipboard.writeText(text);
        onSuccess('Texte copié !');
        setMenuAnchorEl(null);
    };

    // Copier lien
    const handleCopyLink = () => {
        const shareUrl = `${window.location.origin}/post/${post.id}`;
        navigator.clipboard.writeText(shareUrl);
        onSuccess('Lien copié !');
        setMenuAnchorEl(null);
    };

    // Toggle featured
    const handleToggleFeatured = async () => {
        try {
            await api.post(`/liturgical-posts/${post.id}/toggle-featured`);
            onSuccess(post.is_featured ? 'Post retiré des featured' : 'Post mis en featured');
            onRefresh();
        } catch (err) {
            onError('Erreur');
        }
        setMenuAnchorEl(null);
    };

    // Modérer
    const handleModerate = async (status: 'approved' | 'rejected') => {
        try {
            await api.post(`/liturgical-posts/${post.id}/moderate`, { moderation_status: status });
            onSuccess(`Post ${status === 'approved' ? 'approuvé' : 'rejeté'}`);
            onRefresh();
        } catch (err) {
            onError('Erreur');
        }
        setMenuAnchorEl(null);
    };

    // Supprimer post
    const handleDeletePost = async () => {
        try {
            await api.delete(`/liturgical-posts/${post.id}`);
            onSuccess('Post supprimé');
            setDeleteDialogOpen(false);
            onRefresh();
        } catch (err) {
            onError('Erreur lors de la suppression');
        }
    };

    // Masquer post
    const handleHidePost = async () => {
        try {
            await api.post(`/liturgical-posts/${post.id}/hide`);
            onSuccess('Post masqué');
            onRefresh();
        } catch (err) {
            onError('Erreur');
        }
        setMenuAnchorEl(null);
    };

    return (
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'white' }}>
            <CardContent sx={{ p: 2 }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={authorAvatar} sx={{ width: 48, height: 48, bgcolor: typeConfig.color }}>
                            {authorName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{authorName}</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">{formatDate(post.created_at)}</Typography>
                                <Typography variant="caption" color="text.secondary">•</Typography>
                                <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">{getEthiopianDate()}</Typography>
                                <Typography variant="caption" color="text.secondary">•</Typography>
                                {visConfig.icon}
                            </Stack>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {post.is_featured && <Chip icon={<StarIcon sx={{ fontSize: 14 }} />} label="Featured" size="small" color="warning" sx={{ height: 24 }} />}
                        <Chip label={typeConfig.label} size="small" sx={{ height: 24, bgcolor: `${typeConfig.color}15`, color: typeConfig.color, fontWeight: 500 }} />
                        <IconButton size="small" onClick={(e) => setMenuAnchorEl(e.currentTarget)}>
                            <MoreHorizIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Contenu */}
                <Box sx={{ mt: 2 }}>
                    {post.title && <Typography variant="h6" fontWeight={600} gutterBottom>{post.title}</Typography>}
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{post.content}</Typography>
                </Box>

                {/* Médias */}
                {post.media && post.media.length > 0 && <MediaGrid media={post.media} />}

                {/* Stats */}
                <Stack direction="row" spacing={2} sx={{ mt: 2, py: 1 }} alignItems="center">
                    {totalReactions > 0 && (
                        <ReactionSummary
                            reactions={reactionsByType}
                            totalCount={totalReactions}
                        />
                    )}
                    {(post.comments_count || 0) > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={handleToggleComments}>
                            {post.comments_count} commentaire{(post.comments_count || 0) > 1 ? 's' : ''}
                        </Typography>
                    )}
                    {(post.shares_count || 0) > 0 && (
                        <Typography variant="body2" color="text.secondary">{post.shares_count} partage{(post.shares_count || 0) > 1 ? 's' : ''}</Typography>
                    )}
                </Stack>

                <Divider />

                {/* Actions */}
                <Stack direction="row" justifyContent="space-around" sx={{ py: 0.5 }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <ReactionPicker
                            currentReaction={currentReaction}
                            onReact={handleReact}
                            disabled={toggleReaction.isPending}
                        />
                    </Box>
                    <Button
                        startIcon={<ChatBubbleOutlineIcon />}
                        onClick={handleToggleComments}
                        sx={{ flex: 1, color: 'text.secondary', textTransform: 'none' }}
                    >
                        Commenter
                    </Button>
                    <Button
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyText}
                        sx={{ flex: 1, color: 'text.secondary', textTransform: 'none' }}
                    >
                        Copier
                    </Button>
                    <Button
                        startIcon={<ShareIcon />}
                        onClick={handleCopyLink}
                        sx={{ flex: 1, color: 'text.secondary', textTransform: 'none' }}
                    >
                        Partager
                    </Button>
                </Stack>

                {/* Section Commentaires */}
                <Collapse in={showComments}>
                    <Divider />
                    <Box sx={{ pt: 2 }}>
                        {/* Indicateur de réponse */}
                        {replyToAuthor && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: 'primary.50',
                                borderLeft: '3px solid',
                                borderColor: 'primary.main',
                                px: 1.5,
                                py: 0.5,
                                mb: 1,
                                borderRadius: '0 8px 8px 0',
                            }}>
                                <Typography variant="caption" color="primary.main">
                                    En réponse à <strong>{replyToAuthor}</strong>
                                </Typography>
                                <IconButton size="small" onClick={cancelReply}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}

                        {/* Input nouveau commentaire avec mentions */}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Avatar sx={{ width: 32, height: 32 }} />
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <MentionInput
                                    value={newComment}
                                    onChange={(value, mentions) => {
                                        setNewComment(value);
                                        setMentionedUserIds(mentions);
                                    }}
                                    placeholder={replyToAuthor ? `Répondre à ${replyToAuthor}...` : "Écrire un commentaire... (utilisez @ pour mentionner)"}
                                    maxRows={4}
                                    onSubmit={handleSubmitComment}
                                    disabled={submittingComment}
                                    size="small"
                                />
                                <IconButton
                                    size="small"
                                    onClick={handleSubmitComment}
                                    disabled={!newComment.trim() || submittingComment}
                                    sx={{ mt: 0.5 }}
                                >
                                    {submittingComment ? <CircularProgress size={20} /> : <SendIcon color={newComment.trim() ? 'primary' : 'disabled'} />}
                                </IconButton>
                            </Box>
                        </Stack>

                        {/* Liste des commentaires */}
                        {loadingComments ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : comments.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                Aucun commentaire. Soyez le premier à commenter !
                            </Typography>
                        ) : (
                            <Box sx={{ mt: 1 }}>
                                {comments.filter(c => !c.parent_id).map((comment) => (
                                    <CommentItem
                                        key={comment.id}
                                        comment={comment}
                                        currentUserId={currentUserId}
                                        onLike={handleLikeComment}
                                        onDelete={handleDeleteComment}
                                        onEdit={(c) => { setEditCommentDialog(c); setEditCommentText(c.content); }}
                                        onReply={handleReply}
                                        isAdmin={isAdmin}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>

            {/* Menu Options */}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
                {/* Options pour tout le monde */}
                <MenuItem onClick={handleCopyText}>
                    <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Copier le texte" secondary="Copier le contenu du post" />
                </MenuItem>
                <MenuItem onClick={handleCopyLink}>
                    <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Copier le lien" secondary="Partager ce post" />
                </MenuItem>

                <Divider />

                {/* Options pour le propriétaire */}
                {isOwnPost && (
                    <>
                        <MenuItem onClick={() => { setMenuAnchorEl(null); /* TODO: Edit post */ }}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Modifier le post" />
                        </MenuItem>
                        <MenuItem onClick={() => { setMenuAnchorEl(null); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText primary="Supprimer le post" />
                        </MenuItem>
                        <Divider />
                    </>
                )}

                {/* Options admin */}
                {isAdmin && (
                    <>
                        <MenuItem onClick={handleToggleFeatured}>
                            <ListItemIcon>{post.is_featured ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
                            <ListItemText primary={post.is_featured ? 'Retirer des Featured' : 'Mettre en Featured'} />
                        </MenuItem>
                        <MenuItem onClick={() => handleModerate('approved')}>
                            <ListItemIcon><CheckCircleIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary="Approuver" />
                        </MenuItem>
                        <MenuItem onClick={() => handleModerate('rejected')}>
                            <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText primary="Rejeter" />
                        </MenuItem>
                        {!isOwnPost && (
                            <MenuItem onClick={() => { setMenuAnchorEl(null); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>
                                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                <ListItemText primary="Supprimer (Admin)" />
                            </MenuItem>
                        )}
                    </>
                )}

                {/* Options pour les autres */}
                {!isOwnPost && (
                    <>
                        <MenuItem onClick={handleHidePost}>
                            <ListItemIcon><VisibilityOffIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Masquer ce post" />
                        </MenuItem>
                        <MenuItem onClick={() => { setMenuAnchorEl(null); setReportDialogOpen(true); }}>
                            <ListItemIcon><ReportIcon fontSize="small" color="warning" /></ListItemIcon>
                            <ListItemText primary="Signaler" />
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Dialog suppression post */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Supprimer le post ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Cette action est irréversible.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleDeletePost} color="error">Supprimer</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog modification commentaire */}
            <Dialog open={!!editCommentDialog} onClose={() => setEditCommentDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>Modifier le commentaire</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={editCommentText}
                        onChange={(e) => setEditCommentText(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditCommentDialog(null)}>Annuler</Button>
                    <Button onClick={handleEditComment} variant="contained">Enregistrer</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de signalement */}
            <ReportDialog
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                onSubmit={handleReport}
                targetType="post"
                targetId={post.id}
            />
        </Card>
    );
};

// Skeleton loader pour les posts
const PostSkeleton: React.FC = () => (
    <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'white' }}>
        <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="circular" width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="60%" height={16} />
                </Box>
            </Stack>
            <Box sx={{ mt: 2 }}>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="75%" height={20} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2, borderRadius: 2 }} />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Skeleton variant="text" width={60} height={24} />
                <Skeleton variant="text" width={80} height={24} />
            </Stack>
        </CardContent>
    </Card>
);

// Page principale avec React Query
const PostsPage: React.FC = () => {
    const { authState } = useAuth();
    const { success, error: showError } = useNotification();
    const [openCreateDialog, setOpenCreateDialog] = useState(false);

    const currentUserId = authState.user?.id;
    const isAdmin = authState.user?.role === 'Admin' || authState.user?.role === 'SuperAdmin';
    const userEglises = authState.user?.eglises || [];

    // Date éthiopienne actuelle (approximative)
    const today = new Date();
    const currentDate = {
        day: today.getDate(),
        month: today.getMonth() + 1,
        year: today.getFullYear()
    };

    // Utiliser React Query pour le chargement des posts avec cache
    const {
        data,
        isLoading,
        isError,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isRefetching,
    } = useInfinitePosts(undefined, 20);

    // Aplatir les pages de posts
    const posts = useMemo(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap(page => page.posts);
    }, [data?.pages]);

    // Hook pour le scroll infini avec Intersection Observer
    const { loadMoreRef } = useInfiniteScroll({
        onLoadMore: () => fetchNextPage(),
        hasMore: hasNextPage ?? false,
        isLoading: isFetchingNextPage,
        threshold: 300,
    });

    // Rafraîchir les posts
    const handleRefresh = () => {
        refetch();
    };

    // Callback après création de post
    const handlePostCreated = () => {
        setOpenCreateDialog(false);
        refetch();
        success('Post créé avec succès !');
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f0f2f5' }}>
            {/* Header */}
            <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={700}>Posts</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCreateDialog(true)}
                            size="small"
                        >
                            Créer un post
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={isRefetching ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={isLoading || isRefetching}
                            size="small"
                        >
                            Rafraîchir
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {/* Zone scrollable */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {isError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {(error as Error)?.message || 'Erreur lors du chargement des posts'}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ maxWidth: 680, mx: 'auto' }}>
                        {[1, 2, 3].map((i) => (
                            <PostSkeleton key={i} />
                        ))}
                    </Box>
                ) : posts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">Aucun post trouvé</Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: 680, mx: 'auto' }}>
                        {posts.map((post) => (
                            <PostCard
                                key={post.id}
                                post={post as Post}
                                currentUserId={currentUserId}
                                isAdmin={isAdmin}
                                onRefresh={handleRefresh}
                                onSuccess={success}
                                onError={showError}
                            />
                        ))}

                        {/* Sentinel element pour le scroll infini */}
                        <Box ref={loadMoreRef} sx={{ height: 1 }} />

                        {/* Indicateur de chargement */}
                        {isFetchingNextPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {/* Message de fin */}
                        {!hasNextPage && posts.length > 0 && (
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                                Fin des posts
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* Dialog création de post */}
            <CreatePostDialog
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                selectedDate={currentDate}
                onSuccess={handlePostCreated}
                userEglises={userEglises}
            />
        </Box>
    );
};

export default PostsPage;
